import {
  defaultRangeExtractor,
  Range,
  useVirtualizer,
} from '@tanstack/react-virtual';
import {
  Children,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

const DEFAULT_ESTIMATED_ITEM_HEIGHT = 46;
const DEFAULT_OVERSCAN = 5;

interface UseListboxVirtualizerOptions {
  children: ReactNode;
  highlightedIndex: number;
  estimateSize?: number;
  overscan?: number;
}

export function useListboxVirtualizer({
  children,
  highlightedIndex,
  estimateSize = DEFAULT_ESTIMATED_ITEM_HEIGHT,
  overscan = DEFAULT_OVERSCAN,
}: UseListboxVirtualizerOptions) {
  const listRef = useRef<HTMLUListElement | null>(null);

  const childrenArray = useMemo(
    () => Children.toArray(children).filter(Boolean),
    [children],
  );

  const count = childrenArray.length;

  // https://github.com/TanStack/virtual/issues/1119
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => listRef.current,
    estimateSize: () => estimateSize,
    overscan,
    rangeExtractor: (range: Range) => {
      const defaultRange = defaultRangeExtractor(range);
      if (count === 0) return defaultRange;
      const lastIndex = count - 1;
      if (!defaultRange.includes(0)) {
        defaultRange.unshift(0);
      }
      if (!defaultRange.includes(lastIndex)) {
        defaultRange.push(lastIndex);
      }
      return defaultRange;
    },
  });

  useLayoutEffect(() => {
    if (highlightedIndex >= 0 && highlightedIndex < childrenArray.length) {
      virtualizer.scrollToIndex(highlightedIndex, { align: 'auto' });
    }
  }, [highlightedIndex, childrenArray.length, virtualizer]);

  const setListRef = useCallback((el: HTMLUListElement | null) => {
    listRef.current = el;
  }, []);

  return {
    childrenArray,
    setListRef,
    virtualizer,
  };
}
